import * as yup from "yup";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { stringValidation, getStringValidationRequired } from "@/shared/yup";
import type { Note, NoteStatus } from "../types";

export const NOTE_DIRECTIONS: Record<
  NonNullable<Note["direction"]>,
  NonNullable<Note["direction"]>
> = {
  INCOMING: "INCOMING",
  OUTGOING: "OUTGOING",
};

export const DEFAULT_NOTE_TYPES_LABELS: Record<Note["noteType"]["name"], string> = {
  DEFAULT_COMMITMENT: "Commitment",
  DEFAULT_IDEA: "Idea",
  DEFAULT_LIFE_STORY: "Life story",
  DEFAULT_MEETING: "Meeting",
  DEFAULT_NOTE: "Note",
};

export const DEFAULT_NOTE_DIRECTIONS = {
  ALL: "ALL",
  INCOMING: "INCOMING",
  OUTGOING: "OUTGOING",
};

export const DEFAULT_NOTE_DIRECTIONS_LABELS = {
  ALL: "All",
  INCOMING: "Incoming",
  OUTGOING: "Outgoing",
};

export const DEFAULT_NOTE_TYPES: Record<Note["noteType"]["name"], Note["noteType"]["name"]> = {
  DEFAULT_COMMITMENT: "DEFAULT_COMMITMENT",
  DEFAULT_NOTE: "DEFAULT_NOTE",
  DEFAULT_IDEA: "DEFAULT_IDEA",
  DEFAULT_LIFE_STORY: "DEFAULT_LIFE_STORY",
  DEFAULT_MEETING: "DEFAULT_MEETING",
};

export const NOTE_STATUS: Record<NonNullable<NoteStatus>, NonNullable<NoteStatus>> = {
  DONE: "DONE",
  IN_PROGRESS: "IN_PROGRESS",
  TO_DO: "TO_DO",
};

export const NOTE_STATUS_LABELS: Record<NonNullable<Note["status"]>, string | JSX.Element> = {
  DONE: <Trans i18nKey={"common.status.done"} />,
  IN_PROGRESS: <Trans i18nKey={"common.status.inProgress"} />,
  TO_DO: <Trans i18nKey={"common.status.toDo"} />,
};

export const getNoteSchema = () =>
  yup.object().shape({
    id: stringValidation,
    title: getStringValidationRequired().test(
      "title-or-text",
      () => t("validation.fillInTheFieild"),
      function (value) {
        const { text } = this.parent;
        return Boolean(value?.trim() || text?.trim());
      },
    ),
    text: getStringValidationRequired().test(
      "title-or-text",
      () => t("validation.fillText"),
      function (value) {
        const { title } = this.parent;
        return Boolean(value?.trim() || title?.trim());
      },
    ),
    important: yup.boolean().nullable(),
    status: yup
      .mixed<NonNullable<Note["status"]>>()
      .oneOf([NOTE_STATUS.DONE, NOTE_STATUS.IN_PROGRESS, NOTE_STATUS.TO_DO])
      .nullable(),
    noteDate: stringValidation,
    createdDate: stringValidation,
    dueDate: stringValidation,
    noteType: yup
      .object()
      .shape({
        name: yup
          .mixed<Note["noteType"]["name"]>()
          .required(() =>
            t("validation.selectValue", { value: () => t("notesPage.noteType")?.toLowerCase() }),
          )
          .oneOf([
            DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT,
            DEFAULT_NOTE_TYPES.DEFAULT_IDEA,
            DEFAULT_NOTE_TYPES.DEFAULT_NOTE,
            DEFAULT_NOTE_TYPES.DEFAULT_MEETING,
            DEFAULT_NOTE_TYPES.DEFAULT_LIFE_STORY,
          ]),
      })
      .required(),
    personId: stringValidation.test(
      "is-null",
      t("validation.selectValue", { value: t("common.person")?.toLowerCase() }),
      (value) => value !== null,
    ),
    direction: yup
      .mixed<NonNullable<Note["direction"]>>()
      .oneOf([NOTE_DIRECTIONS.INCOMING, NOTE_DIRECTIONS.OUTGOING])
      .nullable(),
    attachments: yup
      .array()
      .of(
        yup.object().shape({
          id: getStringValidationRequired(),
          fileDescription: stringValidation,
          fileDto: yup.object().shape({
            fileId: stringValidation,
            createdDate: stringValidation,
            fileName: stringValidation,
            type: stringValidation,
          }),
        }),
      )
      .required(),
  });
